import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Scale, Landmark, BookOpen, ChevronDown, X } from 'lucide-react';

const services = [
  {
    icon: Shield,
    title: 'Credibility Verification',
    description: 'A verified AINAA membership signals professional standing.',
    detail: 'Recognised by institutions worldwide',
    expanded: {
      heading: 'How Credibility Verification Works',
      body: 'AINAA conducts an independent, multi-step assessment of your creative portfolio, work history, peer references, and professional conduct. Upon successful verification, you receive an AINAA Credential — a mark of professional standing that is recognised by galleries, publishers, production houses, grant bodies, and cultural institutions globally.',
      points: [
        'Portfolio and body-of-work review by discipline-specific assessors',
        'Peer verification from a minimum of three existing AINAA members',
        'Professional conduct history check across public records',
        'Annual renewal to maintain active credential status',
      ],
    },
  },
  {
    icon: Scale,
    title: 'Legal Aid & Advocacy',
    description: 'Legal counsel for contracts, IP protection, and representation.',
    detail: 'Pro bono and subsidised services',
    expanded: {
      heading: 'Legal Support for Creative Professionals',
      body: 'The creative industry is rife with exploitative contracts, intellectual property theft, and disputes that individual artists cannot afford to fight. AINAA provides its members with access to a panel of legal professionals who specialise in arts and entertainment law.',
      points: [
        'Contract review before you sign — for commissions, licensing, and collaborations',
        'Intellectual property protection and dispute resolution',
        'Representation in cases of non-payment, plagiarism, or breach',
        'Pro bono services for members in financial hardship',
      ],
    },
  },
  {
    icon: Landmark,
    title: 'Financial Support',
    description: 'Grants, emergency funding, and financial planning resources.',
    detail: 'Over $2.4M disbursed annually',
    expanded: {
      heading: 'Financial Infrastructure for Artists',
      body: 'Artists often face irregular income, lack of insurance, and no retirement planning. AINAA has built a financial support system that addresses these structural gaps — from emergency grants to long-term financial literacy programmes.',
      points: [
        'Emergency grants for members facing sudden financial hardship',
        'Project-based funding for creative work aligned with the Association\'s mission',
        'Financial literacy workshops covering tax, savings, and retirement',
        'Partnerships with institutions offering subsidised insurance and banking',
      ],
    },
  },
  {
    icon: BookOpen,
    title: 'Professional Development',
    description: 'Structured programmes for advancement and mentorship.',
    detail: 'Year-round programming',
    expanded: {
      heading: 'Continuous Professional Growth',
      body: 'AINAA runs structured development programmes designed to help creative professionals build sustainable careers. From mentorship with senior practitioners to cross-discipline collaboration opportunities, every programme is designed with practical outcomes.',
      points: [
        'Mentorship matching with senior AINAA members in your discipline',
        'Masterclasses and workshops run by industry leaders',
        'Cross-discipline collaboration programmes',
        'Annual member showcase and exhibition opportunities',
      ],
    },
  },
];

const HowItWorksSection = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  return (
    <section
      id="services"
      className="section-padding relative"
      data-testid="how-it-works-section"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="section-label mb-5">Services</p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0F1B2D] tracking-tight">
            What the Association provides
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-5">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              viewport={{ once: true }}
            >
              <button
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                className="inst-card w-full text-left p-6 flex flex-col cursor-pointer group"
                data-testid={`service-card-${index}`}
              >
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-10 h-10 border-2 border-[#0F1B2D] flex items-center justify-center flex-shrink-0">
                    <service.icon size={18} className="text-[#0F1B2D]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-['Playfair_Display'] text-base font-semibold text-[#0F1B2D] mb-1">
                      {service.title}
                    </h3>
                    <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`text-[#9B9589] flex-shrink-0 mt-1 transition-transform duration-200 ${expandedIndex === index ? 'rotate-180' : ''}`}
                  />
                </div>
                <div className="mt-4 pt-3 border-t border-[#D8D3CB]">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#C4A35A] font-bold">
                    {service.detail}
                  </p>
                </div>
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Expanded detail overlay */}
      <AnimatePresence>
        {expandedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-[#0F1B2D]/50 backdrop-blur-sm"
              onClick={() => setExpandedIndex(null)}
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-lg"
            >
              <div className="bg-[#EDECEA] border-2 border-[#0F1B2D] p-8 md:p-10 shadow-xl">
                <button
                  onClick={() => setExpandedIndex(null)}
                  className="absolute top-4 right-4 p-2 text-[#9B9589] hover:text-[#0F1B2D]"
                  data-testid="service-detail-close"
                >
                  <X size={20} />
                </button>

                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 border-2 border-[#C4A35A] flex items-center justify-center">
                    {(() => {
                      const IconComp = services[expandedIndex].icon;
                      return <IconComp size={18} className="text-[#C4A35A]" />;
                    })()}
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#C4A35A] font-bold">
                    {services[expandedIndex].title}
                  </p>
                </div>

                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
                  {services[expandedIndex].expanded.heading}
                </h3>

                <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-6">
                  {services[expandedIndex].expanded.body}
                </p>

                <div className="border-t-2 border-[#D8D3CB] pt-5">
                  <ul className="space-y-3">
                    {services[expandedIndex].expanded.points.map((point) => (
                      <li key={point} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-[#C4A35A] mt-2 flex-shrink-0" />
                        <span className="text-sm font-medium text-[#4A4640] leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default HowItWorksSection;
